/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Filter, Search, ArrowUpRight, Image as ImageIcon, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import Header from "../../components/common/Header";
import "bootstrap/dist/css/bootstrap.min.css";
import "./SearchScreen.css";
import asaImage from "../../assets/asa.png";
import { formatDateISO } from "../../utils/date";
import {
  searchCollaborationsV2,
  SearchResultItem,
  FacetCounts,
} from "../../api/search";
import { listImagesByCollaboration } from "../../api/admin/images";
import { getUserCollaborationById } from "../../api/colaboracoes";
import SpeciesModal from "./SpeciesModal";
import TermsModal from "./TermsModal";
import JSZip from "jszip";
import FlashMessage from "../../components/common/FlashMessage";
import { useFlashMessage } from "../../hooks/useFlashMessage";

// Interface para as opções de filtro
interface OpcaoFiltro {
  label: string;
  count: number;
}

// Props para a seção de filtros
interface PropsSecaoFiltro {
  titulo: string;
  opcoes: OpcaoFiltro[];
  visivel: boolean;
  alternarVisibilidade: () => void;
}

// ===========================
// Utilitária: gera o .txt
// ===========================
const gerarInformacoesImagem = (
  collaborationData: any,
  baseName: string
) => `INFORMAÇÕES DA IMAGEM
========================

CRÉDITOS OBRIGATÓRIOS:
Imagem: ${collaborationData.autor_foto || "-"} (${
  collaborationData.instituicao_autor || "-"
})
Coleta: ${collaborationData.coletor || "-"} (${
  collaborationData.instituicao_coletor || "-"
})

DADOS DA ESPÉCIE:
Código da Imagem: ${baseName}
Espécie: ${collaborationData.nome_especie || "-"}

LOCALIZAÇÃO:
País: ${collaborationData.pais || "-"}
Região: ${collaborationData.regiao || "-"}
Estado: ${collaborationData.estado || "-"}
Município: ${collaborationData.municipio || "-"}
Latitude: ${collaborationData.latitude || "-"}
Longitude: ${collaborationData.longitude || "-"}
Altitude: ${collaborationData.altitude || "-"} m 

DADOS DE COLETA:
Data da Coleta: ${formatDateISO(collaborationData.data)}
Coletor: ${collaborationData.coletor || "-"}
Instituição do Coletor: ${collaborationData.instituicao_coletor || "-"}

DADOS DA FOTOGRAFIA:
Autor da Foto: ${collaborationData.autor_foto || "-"}
Instituição do Autor: ${collaborationData.instituicao_autor || "-"}

LICENÇAS E AUTORIZAÇÕES:
SISBIO: ${collaborationData.SISBIO || "Não informado"}
SISGEN: ${collaborationData.SISGEN || "Não informado"}

OBSERVAÇÕES:
${collaborationData.observacoes || "Nenhuma observação adicional"}

DOWNLOAD:
Termos aceitos em: ${new Date().toISOString()}
Banco de Imagens Insecta - UFRB.
`;

// Componente principal da tela de busca
const SearchScreen: React.FC = () => {
  const { getCsrfToken } = useAuth();
  const { message, type, showMessage, clearMessage } = useFlashMessage(5000);

  // Estados para controle dos filtros e busca
  const [filtrosSelecionados, setFiltrosSelecionados] = useState<string[]>([]);
  const [termoBusca, setTermoBusca] = useState<string>("");
  const [ordenarPor, setOrdenarPor] = useState<string>("Mais recente");

  // Novos estados para busca server-side
  const [searchResponse, setSearchResponse] = useState<{
    results: SearchResultItem[];
    total: number;
    totalPages: number;
    facets: FacetCounts;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // --- Novos estados de data (controlados) ---
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");

  // Estados para controle de visibilidade das seções de filtro
  const [paisVisivel, setPaisVisivel] = useState<boolean>(true);
  const [regiaoVisivel, setRegiaoVisivel] = useState<boolean>(true);
  const [estadoVisivel, setEstadoVisivel] = useState<boolean>(true);
  const [cidadeVisivel, setCidadeVisivel] = useState<boolean>(true);
  const [dataVisivel, setDataVisivel] = useState<boolean>(true);
  const [asaVisivel, setAsaVisivel] = useState<boolean>(true);
  // ladoAsa agora mantém 'all' | 'd' | 'e' (radio)
  const [ladoAsa, setLadoAsa] = useState<string>("all");

  // Estados para controle do modal
  const [modalVisivel, setModalVisivel] = useState(false);
  const [especieSelecionada, setEspecieSelecionada] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDownloadConfirmModal, setShowDownloadConfirmModal] =
    useState(false);
  const [showTermsFromConfirm, setShowTermsFromConfirm] = useState(false);
  const [showMultiDownloadConfirmModal, setShowMultiDownloadConfirmModal] =
    useState(false);

  // Estados para download múltiplo
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [downloadingMultiple, setDownloadingMultiple] = useState(false);

  // Dados para os filtros vêm agora do servidor (facets)
  const facetData = searchResponse?.facets || {
    pais: {},
    regiao: {},
    estado: {},
    municipio: {},
  };
  const toOpcao = (rec: Record<string, number>) =>
    Object.entries(rec)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

  // Memoizar as arrays de facets baseado no conteúdo (não na referência)
  const paisesStr = JSON.stringify(facetData.pais);
  const regioesStr = JSON.stringify(facetData.regiao);
  const estadosStr = JSON.stringify(facetData.estado);
  const cidadesStr = JSON.stringify(facetData.municipio);

  const paises: OpcaoFiltro[] = useMemo(
    () => toOpcao(facetData.pais),
    [paisesStr]
  );

  const regioes: OpcaoFiltro[] = useMemo(
    () => toOpcao(facetData.regiao),
    [regioesStr]
  );

  const estados: OpcaoFiltro[] = useMemo(
    () => toOpcao(facetData.estado),
    [estadosStr]
  );

  const cidades: OpcaoFiltro[] = useMemo(
    () => toOpcao(facetData.municipio),
    [cidadesStr]
  );

  // Calcular filtros categorizados (memoizado para evitar recriação constante)
  const filtrosCategorizados = useMemo(() => {
    const paisLabels = new Set(paises.map((p) => p.label));
    const regiaoLabels = new Set(regioes.map((r) => r.label));
    const estadoLabels = new Set(estados.map((e) => e.label));
    const cidadeLabels = new Set(cidades.map((c) => c.label));

    return {
      pais: filtrosSelecionados.filter((f) => paisLabels.has(f)),
      regiao: filtrosSelecionados.filter((f) => regiaoLabels.has(f)),
      estado: filtrosSelecionados.filter((f) => estadoLabels.has(f)),
      municipio: filtrosSelecionados.filter((f) => cidadeLabels.has(f)),
    };
  }, [filtrosSelecionados, paises, regioes, estados, cidades]);

  // Manipulador de mudança de filtro
  const handleMudancaFiltro = (filtro: string): void => {
    setFiltrosSelecionados((prev) =>
      prev.includes(filtro)
        ? prev.filter((f) => f !== filtro)
        : [...prev, filtro]
    );
    setPage(1); // Reset para primeira página ao filtrar
    setSelectedImages(new Set()); // Limpar seleções ao filtrar
  };

  // Funções para download múltiplo
  const toggleImageSelection = (imageId: string) => {
    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  // Função para extrair o lado da asa do código da imagem
  // Formato: A1C1AD (D=Direita) ou A1C1AE (E=Esquerda)
  const getWingSide = (codigoImagem: string | undefined): "d" | "e" | null => {
    if (!codigoImagem) return null;
    const upperCode = codigoImagem.toUpperCase();
    if (upperCode.endsWith("AD")) return "d";
    if (upperCode.endsWith("AE")) return "e";
    return null;
  };

  // Resultados vêm direto do servidor (já filtrados e paginados)
  // Criar estrutura de imagem cards a partir dos resultados
  // Filtrar imagens baseado no lado da asa selecionado
  const allImageCards = useMemo(() => {
    const results = searchResponse?.results || [];
    const cards: { coll: SearchResultItem; img: any }[] = [];
    results.forEach((coll) => {
      if (coll.imagens && coll.imagens.length > 0) {
        // Filtrar imagens pelo lado da asa
        const imagensFiltradas = coll.imagens.filter((img) => {
          if (ladoAsa === "all") return true; // Mostrar todas
          const imgSide = getWingSide(img.codigo_imagem);
          return imgSide === ladoAsa; // Mostrar apenas as que correspondem ao filtro
        });

        imagensFiltradas.forEach((img) => {
          cards.push({ coll, img });
        });

        // Se não houver imagens que correspondem ao filtro, não exibir a colaboração
      }
    });
    return cards;
  }, [searchResponse, ladoAsa]);

  // Calcular total de imagens e páginas baseado nas imagens (não colaborações)
  const totalImages = allImageCards.length;
  const totalImagePages = Math.ceil(totalImages / pageSize);

  // Contar o mesmo conjunto de imagens da busca, antes da paginação.
  // As opções do servidor continuam sendo usadas para categorizar a seleção.
  const imageFacetCounts = useMemo(() => {
    const counts: FacetCounts = { pais: {}, regiao: {}, estado: {}, municipio: {} };
    const fields = ["pais", "regiao", "estado", "municipio"] as const;
    allImageCards.forEach(({ coll }) => {
      fields.forEach((field) => {
        const label = coll[field];
        if (label) counts[field][label] = (counts[field][label] || 0) + 1;
      });
    });
    return counts;
  }, [allImageCards]);

  const withImageCounts = (options: OpcaoFiltro[], field: keyof FacetCounts) =>
    options.map((option) => ({ ...option, count: imageFacetCounts[field][option.label] || 0 }))
      .sort((a, b) => b.count - a.count);

  // Paginar as imagens no frontend
  const imageCards = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allImageCards.slice(startIndex, endIndex);
  }, [allImageCards, page, pageSize]);

  // Verifica se todas as imagens da página atual estão selecionadas
  const allCurrentPageSelected = useMemo(() => {
    const currentPageIds = imageCards.map(
      (record) =>
        `${record.coll.colaboracao_id}-${
          record.img?.imagem_id || "placeholder"
        }`
    );
    return (
      currentPageIds.length > 0 &&
      currentPageIds.every((id) => selectedImages.has(id))
    );
  }, [imageCards, selectedImages]);

  // Seleciona/desseleciona todas as imagens da página atual (mantém seleções de outras páginas)
  const toggleSelectAllCurrentPage = () => {
    const currentPageIds = imageCards.map(
      (record) =>
        `${record.coll.colaboracao_id}-${
          record.img?.imagem_id || "placeholder"
        }`
    );

    setSelectedImages((prev) => {
      const newSet = new Set(prev);

      if (allCurrentPageSelected) {
        // Desmarcar todas da página atual
        currentPageIds.forEach((id) => newSet.delete(id));
      } else {
        // Marcar todas da página atual
        currentPageIds.forEach((id) => newSet.add(id));
      }

      return newSet;
    });
  };

  const clearAllSelections = () => {
    setSelectedImages(new Set());
  };

  const downloadSelectedImages = async () => {
    if (selectedImages.size === 0) return;

    setDownloadingMultiple(true);
    try {
      const zip = new JSZip();
      let downloadCount = 0;

      // Buscar dados completos para cada imagem selecionada
      for (const record of imageCards) {
        const imageId = `${record.coll.colaboracao_id}-${
          record.img?.imagem_id || "placeholder"
        }`;
        if (!selectedImages.has(imageId)) continue;

        try {
          // Buscar dados completos da colaboração
          let collaborationData: any = record.coll;
          try {
            const token = getCsrfToken() || undefined;
            const fullCollaboration = await getUserCollaborationById(
              record.coll.colaboracao_id,
              token
            );
            collaborationData = {
              ...record.coll,
              ...fullCollaboration,
              colaboracao_id: record.coll.colaboracao_id,
            };
          } catch (error) {
            
          }

          // Download da imagem
          const imgResp = await fetch(record.img.url_imagem, { credentials: "include" });
          const blob = await imgResp.blob();
          const baseName =
            record.img?.codigo_imagem ||
            record.img?.imagem_id ||
            `imagem_${downloadCount + 1}`;

          // Adicionar imagem ao ZIP
          zip.file(`${baseName}.jpg`, blob);

          // Criar arquivo de informações usando utilitária
          const informacoes = gerarInformacoesImagem(
            collaborationData,
            baseName
          );
          zip.file(`${baseName}_INFORMACOES.txt`, informacoes);
          downloadCount++;
        } catch (error) {
          console.error(`Erro ao processar imagem ${imageId}:`, error);
        }
      }

      if (downloadCount > 0) {
        // Gerar e baixar o ZIP
        const content = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(content);
        const link = document.createElement("a");
        link.href = url;
        link.download = `imagens_selecionadas_${downloadCount}_itens.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Limpar seleções e mostrar sucesso
        setSelectedImages(new Set());
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Erro no download múltiplo:", error);
      showMessage("Erro ao realizar download. Tente novamente.", "error");
    } finally {
      setDownloadingMultiple(false);
    }
  };

  // Função para contar os filtros selecionados
  const contarFiltrosSelecionados = (opcoes: OpcaoFiltro[]): number => {
    return opcoes.filter((opcao) => filtrosSelecionados.includes(opcao.label))
      .length;
  };

  // Função para contar todos os filtros selecionados
  const contarTodosFiltrosSelecionados = (): number => {
    return filtrosSelecionados.length + Number(!!dataInicio) + Number(!!dataFim) + Number(ladoAsa !== "all");
  };

  const clearFilters = () => {
    setFiltrosSelecionados([]);
    setDataInicio("");
    setDataFim("");
    setLadoAsa("all");
    setTermoBusca("");
    setPage(1);
    setSelectedImages(new Set());
  };

  // Componente de seção de filtro
  const SecaoFiltro: React.FC<PropsSecaoFiltro> = ({
    titulo,
    opcoes,
    visivel,
    alternarVisibilidade,
  }) => (
    <div className="secao-filtro">
      <button
        type="button"
        className="cabecalho-filtro"
        onClick={alternarVisibilidade}
        aria-expanded={visivel}
      >
        <span>
          {titulo} ({contarFiltrosSelecionados(opcoes)})
        </span>
        <ChevronDown
          size={20}
          style={{
            transform: visivel ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.3s",
          }}
        />
      </button>
      {visivel && (
        <div className="opcoes-filtro">
          {opcoes.length === 0 && <span className="filter-empty">Nenhuma opção disponível</span>}
          {opcoes.map((opcao) => (
            <label key={opcao.label} className="opcao-filtro">
              <input
                type="checkbox"
                checked={filtrosSelecionados.includes(opcao.label)}
                onChange={() => handleMudancaFiltro(opcao.label)}
              />
              <span>{opcao.label}</span>
              <span className="contagem">({opcao.count})</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  // Componente de seção de data (agora controlada)
  const SecaoData: React.FC = () => (
    <div className="secao-filtro">
      <button
        type="button"
        className="cabecalho-filtro"
        onClick={() => setDataVisivel(!dataVisivel)}
        aria-expanded={dataVisivel}
      >
        <span>Data da coleta</span>
        <ChevronDown
          size={20}
          style={{
            transform: dataVisivel ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.3s",
          }}
        />
      </button>
      {dataVisivel && (
        <div className="intervalo-data">
          <div className="container-intervalo-data">
            <div className="wrapper-input-data">
              <input
                type="date"
                aria-label="Data inicial da coleta"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="form-control"
              />
            </div>
            <div className="separador-data">até</div>
            <div className="wrapper-input-data">
              <input
                type="date"
                aria-label="Data final da coleta"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Componente seletor de Lado da Asa (agora radios)
  const SeletorLadoAsa: React.FC = () => (
    <div className="secao-filtro">
      <button
        type="button"
        className="cabecalho-filtro"
        onClick={() => setAsaVisivel(!asaVisivel)}
        aria-expanded={asaVisivel}
      >
        <span>Lado da asa</span>
        <ChevronDown
          size={20}
          style={{
            transform: asaVisivel ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.3s",
          }}
        />
      </button>
      {asaVisivel && (
        <div className="opcoes-filtro">
          <label className="opcao-filtro">
            <input
              type="radio"
              name="ladoAsa"
              checked={ladoAsa === "all"}
              onChange={() => setLadoAsa("all")}
            />
            <span>Todos</span>
          </label>
          <label className="opcao-filtro">
            <input
              type="radio"
              name="ladoAsa"
              checked={ladoAsa === "d"}
              onChange={() =>
                setLadoAsa((prev) => (prev === "d" ? "all" : "d"))
              }
            />
            <span>Direita</span>
          </label>
          <label className="opcao-filtro">
            <input
              type="radio"
              name="ladoAsa"
              checked={ladoAsa === "e"}
              onChange={() =>
                setLadoAsa((prev) => (prev === "e" ? "all" : "e"))
              }
            />
            <span>Esquerda</span>
          </label>
        </div>
      )}
    </div>
  );

  // Usar o total de páginas de imagens (frontend) em vez de colaborações (backend)
  const totalPages = totalImagePages || 1;

  // Effect para buscar colaborações usando a nova API server-side
  // Agora busca TODAS as colaborações (sem paginação no backend)
  // A paginação é feita no frontend sobre as imagens
  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await searchCollaborationsV2({
          termo: termoBusca || undefined,
          pais:
            filtrosCategorizados.pais.length > 0
              ? filtrosCategorizados.pais
              : undefined,
          regiao:
            filtrosCategorizados.regiao.length > 0
              ? filtrosCategorizados.regiao
              : undefined,
          estado:
            filtrosCategorizados.estado.length > 0
              ? filtrosCategorizados.estado
              : undefined,
          municipio:
            filtrosCategorizados.municipio.length > 0
              ? filtrosCategorizados.municipio
              : undefined,
          dataInicio: dataInicio || undefined,
          dataFim: dataFim || undefined,
          ladoAsa: ladoAsa as "d" | "e" | "all",
          ordenar: ordenarPor === "Mais recente" ? "recent" : "oldest",
          page: 1, // Sempre página 1
          pageSize: 1000, // Buscar muitas colaborações para paginar no frontend
        });

        if (!active) return;
        setSearchResponse(response);
        setSelectedImages(new Set()); // Limpar seleções
        setPage(1); // Reset para página 1 quando os filtros mudam
      } catch (e: any) {
        if (!active) return;
        setError("Não foi possível carregar o acervo. Verifique sua conexão e tente novamente.");
        setSearchResponse(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => { active = false; };
  }, [
    termoBusca,
    ordenarPor,
    filtrosCategorizados,
    dataInicio,
    dataFim,
    ladoAsa,
    retryCount,
    // Removido: page, pageSize - paginação agora é apenas frontend
  ]);

  const openSpecies = async (record: {
    coll: SearchResultItem;
    img: any | null;
  }) => {
    const item = record.coll;
    let image = record.img;

    if (!image || !image.url_imagem) {
      try {
        const token = getCsrfToken() || undefined;
        const response = await listImagesByCollaboration(
          item.colaboracao_id,
          token,
          1,
          100
        );
        image =
          response.items.find(
            (i: any) => (i.status || "").toLowerCase() === "approved"
          ) || response.items[0];
      } catch {}
    }

    // Buscar dados completos da colaboração
    let collaborationData: any = item;
    try {
      const token = getCsrfToken() || undefined;
      const fullCollaboration = await getUserCollaborationById(
        item.colaboracao_id,
        token
      );
      collaborationData = {
        ...item,
        ...fullCollaboration,
        colaboracao_id: item.colaboracao_id,
      };
    } catch (error) {
      
    }

    setSelectedImage(image);
    setEspecieSelecionada({
      id: collaborationData.colaboracao_id,
      species: collaborationData.nome_especie,
      code: image?.codigo_imagem || collaborationData.colaboracao_id.toString(),
      country: collaborationData.pais || "-",
      region: collaborationData.regiao || "-",
      state: collaborationData.estado || "-",
      city: collaborationData.municipio || "-",
      latitude: collaborationData.latitude || "-",
      longitude: collaborationData.longitude || "-",
      altitude: collaborationData.altitude || "-",
      collectionDate: formatDateISO(collaborationData.data),
      collector: collaborationData.coletor || "-",
      collectorInstitution: collaborationData.instituicao_coletor || "-",
      photoAuthor: collaborationData.autor_foto || "-",
      photoAuthorInstitution: collaborationData.instituicao_autor || "-",
      sisbio: collaborationData.SISBIO || "-",
      sisgen: collaborationData.SISGEN || "-",
      observations: collaborationData.observacoes || "-",
      imageUrl: image?.url_imagem || asaImage,
    });
    setModalVisivel(true);
  };

  const realizarDownload = async () => {
    if (!selectedImage || !especieSelecionada) return;

    try {
      const zip = new JSZip();
      const imgResp = await fetch(selectedImage.url_imagem, { credentials: "include" });
      const blob = await imgResp.blob();
      const baseName =
        selectedImage.codigo_imagem || selectedImage.imagem_id || "imagem";

      zip.file(`${baseName}.jpg`, blob);

      // Uso da função utilitária para criar o arquivo de informações
      const informacoes = gerarInformacoesImagem(
        {
          autor_foto: especieSelecionada.photoAuthor,
          instituicao_autor: especieSelecionada.photoAuthorInstitution,
          coletor: especieSelecionada.collector,
          instituicao_coletor: especieSelecionada.collectorInstitution,
          nome_especie: especieSelecionada.species,
          pais: especieSelecionada.country,
          regiao: especieSelecionada.region,
          estado: especieSelecionada.state,
          municipio: especieSelecionada.city,
          latitude: especieSelecionada.latitude,
          longitude: especieSelecionada.longitude,
          altitude: especieSelecionada.altitude,
          data: especieSelecionada.collectionDate,
          observacoes: especieSelecionada.observations,
          SISBIO: especieSelecionada.sisbio,
          SISGEN: especieSelecionada.sisgen,
        },
        baseName
      );

      zip.file(`${baseName}_INFORMACOES.txt`, informacoes);
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${baseName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setShowSuccessModal(true);
    } catch (e) {
      console.error(e);
      const link = document.createElement("a");
      link.href = selectedImage.url_imagem;
      link.download = "imagem.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="container-busca">
      <Header />
      {message && (
        <div style={{ padding: "0 20px", marginTop: "10px" }}>
          <FlashMessage message={message} type={type} onClose={clearMessage} />
        </div>
      )}

      <div className="cabecalho-busca">
        <button
          type="button"
          className={`botao-filtro${filtersOpen ? " is-active" : ""}`}
          aria-expanded={filtersOpen}
          aria-controls="search-filters"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <Filter size={20} />
          <span>Filtros{contarTodosFiltrosSelecionados() > 0 ? ` (${contarTodosFiltrosSelecionados()})` : ""}</span>
          <ChevronDown size={16} />
        </button>

        <div className="caixa-busca">
          <Search size={20} />
          <input
            type="text"
            aria-label="Buscar por espécie"
            placeholder="Busque por espécie"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
          />
        </div>

        <div className="ordenar-por">
          <label htmlFor="search-order">Ordenar por</label>
          <select
            id="search-order"
            value={ordenarPor}
            onChange={(e) => setOrdenarPor(e.target.value)}
          >
            <option>Mais recente</option>
            <option>Mais antigo</option>
          </select>
        </div>
      </div>

      {(contarTodosFiltrosSelecionados() > 0 || termoBusca) && (
        <div className="search-active-filters">
          {filtrosSelecionados.map((filter) => (
            <button key={filter} type="button" onClick={() => handleMudancaFiltro(filter)} aria-label={`Remover filtro ${filter}`}>
              {filter}<X size={14} />
            </button>
          ))}
          <button className="clear-search" type="button" onClick={clearFilters}>Limpar busca e filtros</button>
        </div>
      )}

      <div
        className="contagem-resultados"
      >
        <div aria-live="polite">
          {loading
            ? "Buscando no acervo…"
            : error ? "Busca indisponível"
            : `${totalImages} ${
                totalImages === 1
                  ? "imagem encontrada"
                  : "imagens encontradas"
              }`}
        </div>

        {/* Controles de seleção múltipla */}
        {!loading && imageCards.length > 0 && (
          <div className="search-selection-actions">
            {selectedImages.size > 0 && (
              <>
                <span style={{ fontSize: "14px", color: "#666" }}>
                  {selectedImages.size} selecionada(s)
                </span>
                <button
                  className="btn btn-outline-secondary"
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                  onClick={clearAllSelections}
                >
                  Limpar Todas
                </button>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                  onClick={() => setShowMultiDownloadConfirmModal(true)}
                  disabled={downloadingMultiple}
                >
                  {downloadingMultiple
                    ? "Baixando..."
                    : `Baixar (${selectedImages.size})`}
                </button>
              </>
            )}

            <button
              className="btn btn-outline-secondary search-select-page"
              style={{ fontSize: "12px", padding: "4px 8px" }}
              onClick={toggleSelectAllCurrentPage}
            >
              {allCurrentPageSelected
                ? "Desmarcar página"
                : "Selecionar página"}
            </button>
          </div>
        )}
      </div>

      <div className={`conteudo${filtersOpen ? " filters-open" : ""}`}>
        <aside className="filtros" id="search-filters" aria-label="Filtros do acervo">
          <div className="filters-heading"><h2>Refinar busca</h2><Filter size={17} /></div>
          <SecaoFiltro
            titulo="País"
            opcoes={withImageCounts(paises, "pais")}
            visivel={paisVisivel}
            alternarVisibilidade={() => setPaisVisivel(!paisVisivel)}
          />

          <SecaoFiltro
            titulo="Região"
            opcoes={withImageCounts(regioes, "regiao")}
            visivel={regiaoVisivel}
            alternarVisibilidade={() => setRegiaoVisivel(!regiaoVisivel)}
          />

          <SecaoFiltro
            titulo="Estado"
            opcoes={withImageCounts(estados, "estado")}
            visivel={estadoVisivel}
            alternarVisibilidade={() => setEstadoVisivel(!estadoVisivel)}
          />

          <SecaoFiltro
            titulo="Município"
            opcoes={withImageCounts(cidades, "municipio")}
            visivel={cidadeVisivel}
            alternarVisibilidade={() => setCidadeVisivel(!cidadeVisivel)}
          />

          <SecaoData />
          <SeletorLadoAsa />
          <button className="filters-done" type="button" onClick={() => setFiltersOpen(false)}>Ver resultados</button>
        </aside>

        <main className="resultados" aria-label="Resultados da busca" aria-busy={loading}>
          {loading && <div className="grade-imagens search-skeletons" role="status" aria-label="Carregando imagens">
            {Array.from({ length: 6 }, (_, index) => <div className="search-skeleton" key={index}><div /><span /><span /></div>)}
          </div>}
          {error && !loading && <div className="search-empty" role="alert"><ImageIcon size={32} /><h2>Não conseguimos abrir o acervo</h2><p>{error}</p><button type="button" onClick={() => setRetryCount((count) => count + 1)}>Tentar novamente</button></div>}
          {!loading && !error && (
            <div className="grade-imagens">
              {imageCards.map((record) => {
                const imageId = `${record.coll.colaboracao_id}-${
                  record.img ? record.img.imagem_id : "placeholder"
                }`;
                const isSelected = selectedImages.has(imageId);

                return (
                  <article key={imageId} className={`cartao-imagem${isSelected ? " is-selected" : ""}`}>
                    <button
                      type="button"
                      className="wrapper-imagem"
                      onClick={() => openSpecies(record)}
                      aria-label={`Ver detalhes de ${record.coll.nome_especie}`}
                    >
                      <img
                        src={record.img?.url_imagem ? `${record.img.url_imagem}${record.img.url_imagem.includes("?") ? "&" : "?"}variant=thumb` : asaImage}
                        alt={record.coll.nome_especie}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          const target = e.currentTarget;
                          if (!target.dataset.originalFallback && record.img?.url_imagem) {
                            target.dataset.originalFallback = "true";
                            target.src = record.img.url_imagem;
                          } else if (target.getAttribute("src") !== asaImage) {
                            target.src = asaImage;
                          }
                        }}
                      />
                    </button>

                    <div className="info-imagem">
                      <span className="badge">
                        {record.coll.estado || record.coll.pais || "-"}
                      </span>
                      <span className="data">
                        {formatDateISO(record.coll.data)}
                      </span>
                    </div>

                    <h2>{record.coll.nome_especie}</h2>
                    <p className="image-code">{record.img?.codigo_imagem || "Registro do acervo"}</p>

                    {/* Container para botões na parte inferior */}
                    <div className="image-card-actions">
                      <button
                        className="saiba-mais"
                        onClick={() => openSpecies(record)}
                      >
                        Ver detalhes <ArrowUpRight size={16} />
                      </button>

                      {/* Checkbox para seleção */}
                      <label className="image-select">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleImageSelection(imageId);
                          }}
                          aria-label={`Selecionar imagem ${record.img?.codigo_imagem || record.coll.nome_especie}`}
                        />
                        Selecionar
                      </label>
                    </div>
                  </article>
                );
              })}
              {imageCards.length === 0 && !loading && (
                <div className="search-empty"><Search size={32} /><h2>Nenhum registro encontrado</h2><p>Tente outra espécie ou ajuste os filtros para explorar o acervo.</p><button type="button" onClick={clearFilters}>Limpar busca e filtros</button></div>
              )}
            </div>
          )}

          {/* Seção de paginação com seletor de itens por página */}
          {!loading && !error && imageCards.length > 0 && <div className="search-pagination">
            {/* Navegação da paginação */}
            <nav className="search-page-navigation" aria-label="Paginação do acervo">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>

              <span style={{ fontSize: "14px", color: "#666" }}>
                Página {page} de {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Próximo
              </button>
            </nav>

            {/* Seletor de itens por página */}
            <div className="search-page-size">
              <label htmlFor="search-page-size">
                Itens por página:
              </label>
              <select
                id="search-page-size"
                value={pageSize}
                onChange={(e) => {
                  const newPageSize = parseInt(e.target.value);
                  setPageSize(newPageSize);
                  setPage(1);
                  setSelectedImages(new Set());
                }}
              >
                <option value={10}>10</option>
                <option value={12}>12</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>}
        </main>
      </div>

      {/* Modal de espécies */}
      {especieSelecionada && (
        <SpeciesModal
          show={modalVisivel}
          onHide={() => {
            setModalVisivel(false);
            setEspecieSelecionada(null);
            setSelectedImage(null);
          }}
          onRequestDownload={() => setShowDownloadConfirmModal(true)}
          speciesData={especieSelecionada}
        />
      )}

      {/* Modal de confirmação de download múltiplo */}
      {showMultiDownloadConfirmModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowMultiDownloadConfirmModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Download Múltiplo</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMultiDownloadConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Ao realizar o download de {selectedImages.size} imagem(ns),
                  você concorda com os{" "}
                  <span
                    style={{
                      color: "#007bff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => {
                      setShowTermsFromConfirm(true);
                      setShowTermsModal(true);
                    }}
                  >
                    termos e condições
                  </span>{" "}
                  de uso do banco de imagens Insecta.
                </p>
                <p className="mb-0" style={{ fontSize: "14px", color: "#666" }}>
                  É obrigatório creditar os autores ao utilizar as imagens.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMultiDownloadConfirmModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowMultiDownloadConfirmModal(false);
                    downloadSelectedImages();
                  }}
                >
                  Concordo e Baixar ({selectedImages.size})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de download */}
      {showDownloadConfirmModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowDownloadConfirmModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar Download</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDownloadConfirmModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Ao realizar o download desta imagem, você concorda com os{" "}
                  <span
                    style={{
                      color: "#007bff",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => {
                      setShowTermsFromConfirm(true);
                      setShowTermsModal(true);
                    }}
                  >
                    termos e condições
                  </span>{" "}
                  de uso do banco de imagens Insecta.
                </p>
                <p className="mb-0" style={{ fontSize: "14px", color: "#666" }}>
                  É obrigatório creditar os autores ao utilizar as imagens.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDownloadConfirmModal(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDownloadConfirmModal(false);
                    realizarDownload();
                  }}
                >
                  Concordo e Baixar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de termos e condições */}
      <TermsModal
        show={showTermsModal}
        onHide={() => {
          setShowTermsModal(false);
          if (showTermsFromConfirm) {
            setShowTermsFromConfirm(false);
          }
        }}
        onAccept={() => {
          setShowTermsModal(false);
          if (showTermsFromConfirm) {
            setShowTermsFromConfirm(false);
          }
        }}
        imageUrl={selectedImage?.url_imagem || ""}
      />

      {showSuccessModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content p-3 text-center">
              <div style={{ fontSize: 48 }}>✅</div>
              <h5 className="mt-3">Download realizado com sucesso!</h5>
              <p className="mb-3" style={{ fontSize: 12 }}>
                {selectedImages.size > 0
                  ? `${selectedImages.size} imagem(ns) baixada(s). Lembre-se de creditar os autores ao utilizar as imagens.`
                  : "Lembre-se de creditar o autor ao utilizar a imagem."}
              </p>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowSuccessModal(false)}
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchScreen;
